import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Video, AlertTriangle, Clock, RefreshCw, Loader2, Mail, MessageSquare,
  CheckCircle2, Activity, Building2, ShieldAlert, Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BAND_PILL = {
  high: 'bg-red-50 text-red-700 border-red-200',
  medium: 'bg-amber-50 text-amber-700 border-amber-200',
  low: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

const ADMIN_NAV = [
  { icon: Activity, label: 'Operations Executive', href: '/operations/executive' },
  { icon: Building2, label: 'Referral Sources', href: '/operations/referral-sources' },
  { icon: ShieldAlert, label: 'EVV Compliance', href: '/admin/evv' },
  { icon: Users, label: 'Clinician Retention', href: '/admin/clinician-retention' },
  { icon: Video, label: 'Telehealth no-shows', active: true },
];

export default function AdminTelehealthNoShows() {
  const { getAuthHeader } = useAuth();
  const [window, setWindow] = useState(14);
  const [band, setBand] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/telehealth/no-show-upcoming`, {
        ...getAuthHeader(),
        params: { window_days: window, risk_band: band || undefined, limit: 100 },
      });
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load no-show predictions');
    } finally {
      setLoading(false);
    }
  }, [window, band, getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const bandCounts = data?.band_counts || { high: 0, medium: 0, low: 0 };
  const total = data?.total_appointments || 0;

  return (
    <DashboardShell
      navItems={ADMIN_NAV}
      title="Telehealth no-show predictor"
      subtitle="Per-appointment probability + the exact reminder cadence that protects each visit. Pre-empt no-shows before they happen."
      testId="admin-noshow-page"
    >
      {/* CONTROLS */}
      <div className="cn-card p-4 mb-6 flex flex-wrap items-center gap-3" data-testid="noshow-controls">
        <div className="flex items-center gap-2">
          <span className="text-xs text-cn-text-muted font-semibold">Window</span>
          <select value={window} onChange={(e) => setWindow(Number(e.target.value))} className="border border-cn-border rounded-lg px-2 py-1.5 text-sm bg-white" data-testid="noshow-window">
            {[7, 14, 30, 60].map((d) => <option key={d} value={d}>Next {d} days</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-cn-text-muted font-semibold">Risk band</span>
          <select value={band} onChange={(e) => setBand(e.target.value)} className="border border-cn-border rounded-lg px-2 py-1.5 text-sm bg-white" data-testid="noshow-band">
            <option value="">All bands</option>
            <option value="high">High only</option>
            <option value="medium">Medium only</option>
            <option value="low">Low only</option>
          </select>
        </div>
        <Button onClick={load} disabled={loading} className="cn-btn cn-btn-ghost !py-2" data-testid="noshow-refresh">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {loading && <div className="cn-card p-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-cn-primary mx-auto" /></div>}

      {!loading && data && (
        <>
          {/* KPI TILES */}
          <div className="grid sm:grid-cols-4 gap-4 mb-6" data-testid="noshow-kpis">
            <Tile label="Telehealth visits" value={total} icon={Video} sub={`Next ${window} days`} testid="noshow-kpi-total" />
            <Tile label="High risk" value={bandCounts.high} icon={AlertTriangle} tone="red" sub="≥ 65% no-show probability" testid="noshow-kpi-high" />
            <Tile label="Medium" value={bandCounts.medium} icon={Clock} tone="amber" sub="40-65% probability" testid="noshow-kpi-medium" />
            <Tile label="Low" value={bandCounts.low} icon={CheckCircle2} tone="emerald" sub="< 40% probability" testid="noshow-kpi-low" />
          </div>

          {/* TABLE */}
          <div className="cn-card overflow-hidden" data-testid="noshow-table-card">
            <div className="p-5 border-b border-cn-border">
              <h4 className="font-display font-semibold text-base text-cn-text">Upcoming visits ({data?.appointments?.length || 0})</h4>
              <p className="text-xs text-cn-text-muted mt-0.5">Sorted by no-show probability. The recommendation column is your suggested reminder cadence.</p>
            </div>
            {data?.appointments?.length === 0 ? (
              <div className="p-10 text-center" data-testid="noshow-empty">
                <Video className="w-6 h-6 text-cn-text-muted mx-auto mb-2" />
                <p className="text-sm text-cn-text-muted">No upcoming telehealth visits in this window.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="noshow-table">
                  <thead className="bg-cn-bone-50 border-b border-cn-border text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">
                    <tr className="text-left">
                      <th className="px-5 py-3">When</th>
                      <th className="px-3 py-3 text-right">Probability</th>
                      <th className="px-3 py-3">Band</th>
                      <th className="px-3 py-3">Top driver</th>
                      <th className="px-3 py-3">Recommended action</th>
                      <th className="px-3 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-cn-border">
                    {(data.appointments || []).map((a) => {
                      const dt = a.scheduled_dt ? new Date(a.scheduled_dt) : null;
                      return (
                        <tr key={a.appointment_id} className="hover:bg-cn-bone-50/40" data-testid={`noshow-row-${a.appointment_id}`}>
                          <td className="px-5 py-2.5">
                            <p className="font-semibold text-cn-text">{dt ? dt.toLocaleString() : '—'}</p>
                            <p className="text-[10px] text-cn-text-muted">Appt: {a.appointment_id.slice(0, 8)}…</p>
                          </td>
                          <td className="px-3 py-2.5 text-right font-display font-bold">
                            {(a.no_show_probability * 100).toFixed(0)}%
                          </td>
                          <td className="px-3 py-2.5">
                            <span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold ${BAND_PILL[a.risk_band]}`} data-testid={`noshow-band-${a.appointment_id}`}>{a.risk_band}</span>
                          </td>
                          <td className="px-3 py-2.5 text-cn-text-muted text-xs">{a.top_factors[0]?.explain}</td>
                          <td className="px-3 py-2.5 text-cn-text text-xs">{a.recommendation}</td>
                          <td className="px-3 py-2.5">
                            {a.risk_band === 'high' ? (
                              <Button className="cn-btn cn-btn-primary !py-1.5 text-xs" data-testid={`noshow-action-${a.appointment_id}`}>
                                <MessageSquare className="w-3 h-3" /> Pre-empt
                              </Button>
                            ) : (
                              <Button className="cn-btn cn-btn-ghost !py-1.5 text-xs" data-testid={`noshow-remind-${a.appointment_id}`}>
                                <Mail className="w-3 h-3" /> Remind
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* MODEL EXPLAINER */}
          <div className="cn-card p-5 mt-6" data-testid="noshow-model-explainer">
            <h4 className="font-display font-semibold text-sm text-cn-text mb-2">How the predictor works</h4>
            <p className="text-xs text-cn-text-muted mb-3">Each factor contributes a 0-1 risk weight to the composite probability:</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {Object.entries(data?.weights || {}).map(([k, w]) => (
                <div key={k} className="flex items-center justify-between text-xs">
                  <span className="text-cn-text">{k.replace(/_/g, ' ')}</span>
                  <span className="font-bold text-cn-primary">{(w * 100).toFixed(0)}%</span>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-cn-text-muted mt-3">
              Heuristic & fully auditable — no opaque ML so compliance and bias-audit teams can verify each prediction.
            </p>
          </div>
        </>
      )}
    </DashboardShell>
  );
}

function Tile({ label, value, sub, icon: Icon, tone = 'neutral', testid }) {
  const iconCls = {
    neutral: 'text-cn-primary',
    red: 'text-red-600',
    amber: 'text-amber-600',
    emerald: 'text-emerald-600',
  }[tone];
  return (
    <div className="cn-card p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">{label}</span>
        <Icon className={`w-4 h-4 ${iconCls}`} />
      </div>
      <p className="font-display font-bold text-3xl text-cn-text dark:text-white">{value}</p>
      {sub && <p className="text-xs text-cn-text-muted mt-1">{sub}</p>}
    </div>
  );
}
